import { useCallback, useMemo, useState } from 'react';
import { isEmpty } from 'lodash';
import { BaseExcelExportDialogErrors } from '../types';

export interface UseExcelExportValidationProps<T extends BaseExcelExportDialogErrors> {
  initialErrors: T;
}

export const useExcelExportValidation = <T extends BaseExcelExportDialogErrors>({ 
  initialErrors 
}: UseExcelExportValidationProps<T>) => {
  const [errors, setErrors] = useState<T>(initialErrors);

  const setError = useCallback((field: keyof T, errorMessages: string[]) => {
    setErrors(prev => ({
      ...prev,
      [field]: errorMessages,
    }));
  }, []);

  const clearError = useCallback((field: keyof T) => {
    setErrors(prev => ({
      ...prev,
      [field]: undefined,
    }));
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors(initialErrors);
  }, [initialErrors]);

  const hasErrors = useMemo(() => !isEmpty(errors), [errors]);

  const hasFieldError = useCallback((field: keyof T) => !!(errors[field] && errors[field]!.length > 0), [errors]);

  return {
    errors,
    setErrors,
    setError,
    clearError,
    clearAllErrors,
    hasErrors,
    hasFieldError,
  };
};
